import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { defaultFilters, getDefaultFilters } from '../defaultFilters';
import { useMovementsSearchParam } from './useMovementsSearchParam';
import { activeNetworkSelector } from '../../../redux/selectors';
import { trackEvent } from '../../../helpers/analytics';

export const useMovementsFilters = () => {
  const [queryParams, setQueryParams] = useMovementsSearchParam({ filters: defaultFilters });
  const activeNetwork = useSelector(activeNetworkSelector);

  const { filters } = queryParams;

  const setFilters = useCallback(
    (newFilters) => {
      Object.keys(newFilters).forEach((filterName) => {
        const filterValue = newFilters[filterName];
        let selectedValues = filterValue;
        if (Array.isArray(filterValue)) {
          selectedValues = filterValue.map((item) => item?.value ?? item);
        } else if (filterValue && typeof filterValue === 'object' && 'value' in filterValue) {
          selectedValues = filterValue.value;
        }

        trackEvent('movements:filter_change', {
          collection_id: activeNetwork?.id,
          filter_name: filterName,
          selected_values: selectedValues,
        });
      });

      setQueryParams({
        ...queryParams,
        filters: { ...filters, ...newFilters },
        page: 1,
      });
    },
    [queryParams, filters, setQueryParams, activeNetwork?.id],
  );

  const resetFilters = useCallback(() => {
    setQueryParams({
      ...queryParams,
      filters: getDefaultFilters(),
      page: 1,
    });
  }, [queryParams, setQueryParams]);

  const filtersCount = useMemo(() => {
    let count = 0;
    const skipKeys = new Set(['sortBy', 'sortDirection', 'dateTo']);
    Object.keys(filters).forEach((key) => {
      if (skipKeys.has(key)) return;

      const value = filters[key];
      const defaultValue = defaultFilters[key];

      if (value === null || value === undefined) return;
      if (value === defaultValue) return;

      if (Array.isArray(value)) {
        if (value.length > 0) count += 1;
        return;
      }

      if (typeof value === 'object') {
        if (value.value === '' || value.value === null) return;
        if (Object.keys(value).length > 0) count += 1;
        return;
      }

      if (value === '') return;

      count += 1;
    });

    return count;
  }, [filters]);

  return {
    filters,
    setFilters,
    resetFilters,
    filtersCount,
    queryParams,
    setQueryParams,
  };
};
